import { ActivityStatus } from '@showbie-socrative/socrative-utils';
import { QueryClient } from '@tanstack/react-query';
import { decamelizeKeys } from 'xcase';

import { studentResponseCacheUpdater } from '~teacher/components/ActivityResult/hooks/useActivityLiveEvent/pubnubMessage/cacheUpdaters/studentResponseCacheUpdater';
import { activityStatusCacheUpdater } from '~teacher/hook';

import { getLongMessage } from '../../../../../../api/longMessage/longMessageApi';
import { studentFinishedCacheUpdater } from './cacheUpdaters/studentFinishedCacheUpdater';
import { studentNameSetCacheUpdater } from './cacheUpdaters/studentNameSetCacheUpdater';
import { PubNubMessageKey, PubnubMessage } from './types';

// snake case keys from messages are mapped to camelCase local versions.

export const handleMessageEvent = ({
  queryClient,
  message,
  isRosteredRoom,
  activityInstanceId,
}: {
  queryClient: QueryClient;
  message: PubnubMessage;
  isRosteredRoom: boolean;
  activityInstanceId: number;
}) => {
  if (__DEV__) {
    console.debug(`isRostered: ${isRosteredRoom}`);
    console.debug(message);
  }

  switch (message.key) {
    case PubNubMessageKey.StudentFinished: {
      const {
        data: { user_uuid },
      } = message;

      studentFinishedCacheUpdater({
        queryClient: queryClient,
        activityInstanceId: activityInstanceId,
        userUuid: user_uuid,
      });

      break;
    }
    case PubNubMessageKey.StudentResponse: {
      const { data } = message;

      studentResponseCacheUpdater({
        queryClient: queryClient,
        activityInstanceId: activityInstanceId,
        rawResponse: data,
      });

      break;
    }
    case PubNubMessageKey.StudentNameSet: {
      const {
        data: { student_name, user_uuid, user_previous_uuid },
      } = message;

      studentNameSetCacheUpdater({
        queryClient: queryClient,
        activityInstanceId: activityInstanceId,
        userUuid: user_uuid,
        userPreviousUuid: user_previous_uuid,
        studentName: student_name,
        isRosteredRoom: isRosteredRoom,
      });
      break;
    }
    case PubNubMessageKey.LongMessage: {
      const {
        data: { longMessageId },
      } = message;

      getLongMessage(longMessageId, activityInstanceId)
        .then((results) => {
          if (results?.data?.message?.data) {
            studentResponseCacheUpdater({
              queryClient: queryClient,
              activityInstanceId: activityInstanceId,
              rawResponse: decamelizeKeys(results.data.message.data),
            });
          }
        })
        .catch((error) => {
          console.error(error);
        });
      break;
    }
    case PubNubMessageKey.ActivityChange: {
      const { data } = message;

      switch (data) {
        case 'activity_paused': {
          activityStatusCacheUpdater({
            queryClient: queryClient,
            activityStatus: ActivityStatus.Paused,
            activityInstanceId: activityInstanceId,
          });
          break;
        }
        case 'activity_resumed': {
          activityStatusCacheUpdater({
            queryClient: queryClient,
            activityStatus: ActivityStatus.Running,
            activityInstanceId: activityInstanceId,
          });
          break;
        }
        case 'activity_ended': {
          activityStatusCacheUpdater({
            queryClient: queryClient,
            activityStatus: ActivityStatus.Finished,
            activityInstanceId: activityInstanceId,
          });
          break;
        }
        default: {
          if (__DEV__) {
            console.debug('Unhandled ActivityChange message', message);
          }
          break;
        }
      }
      break;
    }
    default: {
      if (__DEV__) {
        console.debug('Unhandled Pubnub message', message);
      }
      break;
    }
  }
};
